import React, { useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  IconButton,
  TextField,
  Typography
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import AddIcon from "@material-ui/icons/Add";
import DragIndicatorIcon from "@material-ui/icons/DragIndicator";

import {
  DragDropContext,
  Droppable,
  Draggable
} from "react-beautiful-dnd";
import recipientsService from "../../../../services/recipientsService";

export default function PublishDialog({
  open,
  onClose,
  onPublish,
  recipients,
  setRecipients,
  templateId
}) {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const handleDragEnd = (result) => {
    const { source, destination } = result;
    if (!destination || destination.index === source.index) {
      return;
    }
    const updated = Array.from(recipients);
    const [moved] = updated.splice(source.index, 1);
    updated.splice(destination.index, 0, moved);

    updated.forEach((item, idx) => {
      item.order = idx + 1;
    });
    setRecipients(updated);
  };

  const handleRemove = (index) => {
    const updated = [...recipients];
    updated.splice(index, 1);
    updated.forEach((item, idx) => (item.order = idx + 1));
    setRecipients(updated);
  };

  const handleAddRecipient = () => {
    const updated = [
      ...recipients,
      { name: "", email: "", order: recipients.length + 1 }
    ];
    setRecipients(updated);
  };

  const handleChangeName = (index, newName) => {
    const updated = [...recipients];
    updated[index].name = newName;
    setRecipients(updated);
  };

  const handleChangeEmail = (index, newEmail) => {
    const updated = [...recipients];
    updated[index].email = newEmail;
    setRecipients(updated);
  };

  //---------------------------
  // Validación del formulario
  //---------------------------
  const isFormValid = useMemo(() => {
    return (
      recipients.length > 0 &&
      recipients.every((r) =>
        r.name.trim() !== "" &&
        emailRegex.test(r.email.trim())
      )
    );
  }, [recipients]);

  const handlePublishClick = () => {
    // Save recipients to localStorage before publishing
    const recipientsToSave = recipients.map((recipient, index) => ({
      id: recipient.id || Date.now().toString() + index,
      name: recipient.name.trim(),
      email: recipient.email.trim(),
      order: recipient.order || index + 1,
      createdAt: new Date().toISOString()
    }));
    
    recipientsService.saveRecipients(recipientsToSave);
    
    // Call the original onPublish function with templateId for redirection
    onPublish(templateId);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Publish</DialogTitle>

      <DialogContent dividers>
        <Typography variant="body2" gutterBottom>
          Add the information of the recipients. You can arrange the order by 
          dragging the handle on the left. Once done, click the Publish button.
        </Typography>

        <Box mt={2}>
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="droppable-recipients">
              {(providedDroppable) => (
                <div
                  ref={providedDroppable.innerRef}
                  {...providedDroppable.droppableProps}
                >
                  {recipients.map((r, index) => (
                    <Draggable
                      key={String(r.order)} 
                      draggableId={String(r.order)}
                      index={index}
                    >
                      {(providedDraggable, snapshot) => (
                        <Box
                          ref={providedDraggable.innerRef}
                          {...providedDraggable.draggableProps}
                          display="flex"
                          alignItems="center"
                          justifyContent="flex-end"
                          mb={2}
                          style={{
                            background: snapshot.isDragging ? "#f2f2f2" : "transparent",
                            borderRadius: 4,
                            ...providedDraggable.draggableProps.style
                          }}
                        >
                          <IconButton
                            size="small"
                            style={{ marginRight: 8 }}
                            {...providedDraggable.dragHandleProps}
                          >
                            <DragIndicatorIcon fontSize="small" />
                          </IconButton>

                          <Box
                            width={30}
                            textAlign="center"
                            mr={1}
                            border={1}
                            borderColor={"#D9D9D9"}
                            marginTop={2}
                          >
                            <Typography variant="subtitle1">
                              {index + 1}
                            </Typography>
                          </Box>

                          <Box flex={1} mr={1} display="flex" gridGap={16}>
                            <div style={{ flex: 1 }}>
                              <Typography
                                variant="subtitle2"
                                style={{ fontWeight: "bold", marginBottom: 4 }}
                              >
                                Name
                              </Typography>
                              <TextField
                                variant="outlined"
                                size="small"
                                fullWidth
                                value={r.name}
                                onChange={(e) => handleChangeName(index, e.target.value)}
                                style={{ marginBottom: 8 }}
                              />
                            </div>
                            <div style={{ flex: 1 }}>
                              <Typography
                                variant="subtitle2"
                                style={{ fontWeight: "bold", marginBottom: 4 }}
                              >
                                Email
                              </Typography>
                              <TextField
                                variant="outlined"
                                size="small"
                                fullWidth
                                value={r.email}
                                onChange={(e) => handleChangeEmail(index, e.target.value)}
                              />
                            </div>
                          </Box>

                          <IconButton
                            size="medium"
                            style={{ marginTop: 8 }}
                            onClick={() => handleRemove(index)}
                          >
                            <CloseIcon fontSize="inherit" />
                          </IconButton>
                        </Box>
                      )}
                    </Draggable>
                  ))}
                  {providedDroppable.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </Box>

        <Button
          onClick={handleAddRecipient}
          variant="outlined"
          color="primary"
          startIcon={<AddIcon />}
        >
          Add Recipient
        </Button>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="default" style={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Button
          onClick={handlePublishClick}
          color="primary"
          variant="contained"
          style={{ textTransform: "none" }}
          disabled={!isFormValid}
        >
          Publish
        </Button>
      </DialogActions>
    </Dialog>
  );
}
